import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Sparkles,
  Target,
  Lightbulb,
  Copy,
  Check,
  MessageSquare,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type RecoveryApproach = "soft" | "medium" | "aggressive";

export interface LeadRecoveryData {
  context_summary: string;
  objections: string[];
  recommended_approach: RecoveryApproach;
  recommended_action: string;
  reasoning: string;
  messages: {
    soft: string;
    medium: string;
    aggressive: string;
  };
  message_count?: number;
  lead_name?: string;
}

interface LeadRecoverySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: LeadRecoveryData | null;
  isLoading: boolean;
  onInsertToChat?: (text: string) => void;
}

const approachConfig: Record<
  RecoveryApproach,
  { label: string; description: string; color: string }
> = {
  soft: {
    label: "Suave",
    description: "Reabre o diálogo com leveza, sem pressão",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  medium: {
    label: "Média",
    description: "Equilibra empatia e condução comercial",
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  aggressive: {
    label: "Mais assertiva",
    description: "Direta e firme, quebra objeções com urgência",
    color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
};

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
      <Icon className="h-4 w-4" />
      {children}
    </div>
  );
}

function MessageSuggestionCard({
  approach,
  value,
  onChange,
  isRecommended,
  onCopy,
  onInsert,
}: {
  approach: RecoveryApproach;
  value: string;
  onChange: (value: string) => void;
  isRecommended: boolean;
  onCopy: () => Promise<void>;
  onInsert?: () => void;
}) {
  const config = approachConfig[approach];
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Parent shows toast on failure
    }
  };

  return (
    <div
      className={cn(
        "rounded-lg border p-4 space-y-3",
        isRecommended && "border-primary/50 bg-primary/5 ring-1 ring-primary/20",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className={cn("font-medium", config.color)}>
            {config.label}
          </Badge>
          {isRecommended && (
            <Badge variant="outline" className="border-primary text-primary">
              Recomendada
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8" onClick={handleCopy} disabled={!value.trim()}>
            {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
            {copied ? "Copiado" : "Copiar"}
          </Button>
          {onInsert && (
            <Button variant="outline" size="sm" className="h-8" onClick={onInsert} disabled={!value.trim()}>
              <MessageSquare className="mr-1 h-3.5 w-3.5" />
              Usar no chat
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{config.description}</p>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="resize-none text-sm"
        placeholder="Mensagem sugerida..."
      />
    </div>
  );
}

export function LeadRecoverySheet({
  open,
  onOpenChange,
  data,
  isLoading,
  onInsertToChat,
}: LeadRecoverySheetProps) {
  const { toast } = useToast();
  const [editedMessages, setEditedMessages] = useState({
    soft: "",
    medium: "",
    aggressive: "",
  });

  useEffect(() => {
    if (data?.messages) {
      setEditedMessages({
        soft: data.messages.soft || "",
        medium: data.messages.medium || "",
        aggressive: data.messages.aggressive || "",
      });
    } else if (!open) {
      setEditedMessages({ soft: "", medium: "", aggressive: "" });
    }
  }, [data, open]);

  const copyText = async (text: string) => {
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Mensagem copiada",
        description: "Cole no chat para enviar ao cliente.",
      });
    } catch {
      toast({
        title: "Não foi possível copiar",
        description: "Permita acesso à área de transferência.",
        variant: "destructive",
      });
      throw new Error("clipboard_write_failed");
    }
  };

  const insertText = (text: string) => {
    if (!onInsertToChat || !text.trim()) return;
    onInsertToChat(text.trim());
    onOpenChange(false);
    toast({
      title: "Mensagem no chat",
      description: "Revise e envie quando estiver pronto.",
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Assistente de Recuperação de Leads
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {!isLoading && data && (
            <>
              {data.context_summary && (
                <div className="space-y-2">
                  <SectionTitle icon={Target}>Contexto</SectionTitle>
                  <p className="text-sm leading-relaxed text-foreground">{data.context_summary}</p>
                </div>
              )}

              {data.objections && data.objections.length > 0 && (
                <div className="space-y-2">
                  <SectionTitle icon={ShieldAlert}>Objeções identificadas</SectionTitle>
                  <ul className="space-y-1.5">
                    {data.objections.map((objection, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                        {objection}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
                <SectionTitle icon={Lightbulb}>Melhor decisão</SectionTitle>
                <p className="text-sm font-medium text-foreground">{data.recommended_action}</p>
                {data.reasoning && (
                  <p className="text-sm leading-relaxed text-muted-foreground">{data.reasoning}</p>
                )}
                <Badge variant="secondary" className={approachConfig[data.recommended_approach].color}>
                  Abordagem recomendada: {approachConfig[data.recommended_approach].label}
                </Badge>
              </div>

              <div className="space-y-4">
                <SectionTitle icon={MessageSquare}>Mensagens sugeridas</SectionTitle>
                <p className="text-xs text-muted-foreground">
                  Edite as mensagens antes de copiar ou usar no chat.
                </p>

                {(["soft", "medium", "aggressive"] as RecoveryApproach[]).map((approach) => (
                  <MessageSuggestionCard
                    key={approach}
                    approach={approach}
                    value={editedMessages[approach]}
                    onChange={(value) =>
                      setEditedMessages((prev) => ({ ...prev, [approach]: value }))
                    }
                    isRecommended={data.recommended_approach === approach}
                    onCopy={() => copyText(editedMessages[approach])}
                    onInsert={onInsertToChat ? () => insertText(editedMessages[approach]) : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
